/**
 * The shape of the signed-in chrome, decided once.
 *
 * There is no top bar. The brand, the navigation and the account controls are
 * three parts of one rail, so all three have to agree about whether labels are
 * showing — and the decision is not a media query, because at 768 the user can
 * expand the rail by hand and `ui.navExpanded` is not available to CSS.
 *
 * It lived inside `AppNav` while the nav was the only thing in the rail. A
 * banner and an account panel that each re-derived it would be three copies of
 * one rule, and the failure mode is a 15rem rail holding icon-only items.
 *
 * DESIGN.md section 12 is the source of the breakpoints:
 *
 *   | 360  | bottom tab bar, 4 max   | rail is a top bar |
 *   | 768  | collapsible rail, icons | toggle offered    |
 *   | 1024 | persistent rail, icons  | no toggle         |
 *   | 1280 | persistent rail, labels |                   |
 */

import { computed, type ComputedRef } from 'vue'

import { useViewport } from '@/lib/viewport'
import { useUiStore } from '@/stores/ui'

export interface RailShape {
  /** Below 768: the nav is a fixed bottom tab bar and the rail is a top bar. */
  phone: ComputedRef<boolean>
  /** 768–1023 only. Above that the rail is persistent, so there is nothing to toggle. */
  collapsible: ComputedRef<boolean>
  /**
   * Labels are visible: 1280 and up, or the user expanded the rail. Everything
   * in the rail that has a long form and a short one keys off this one flag —
   * the nav labels, the wordmark beside the mark, and the account panel's shape.
   */
  labeled: ComputedRef<boolean>
}

export function useRailShape (): RailShape {
  const viewport = useViewport()
  const ui = useUiStore()

  const phone = computed(() => viewport.below('tablet'))
  const labeled = computed(() => !phone.value && (viewport.atLeast('target') || ui.navExpanded))

  return {
    phone,
    labeled,
    collapsible: computed(() => !phone.value && viewport.below('desktop'))
  }
}
